import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Users, Bell, Trash2, Mail, Phone, RefreshCw, Ticket } from "lucide-react";

export default function Waitlist() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try { const { data } = await api.get("/admin/waitlist"); setRows(data || []); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const notify = async (id) => {
    try { const { data } = await api.post(`/admin/waitlist/${id}/notify`); toast.success(`${data.notified} Person(en) benachrichtigt`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Benachrichtigung fehlgeschlagen"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Eintrag wirklich löschen?")) return;
    try { await api.delete(`/admin/waitlist/${id}`); toast.success("Gelöscht"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler"); }
  };

  const grouped = rows.reduce((m, r) => {
    const k = `${r.showtime_date || ""}·${r.showtime_start || ""}·${r.movie_title || ""}`;
    (m[k] ||= []).push(r); return m;
  }, {});

  return (
    <div className="space-y-6" data-testid="waitlist-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A398]">Nachfrage</div>
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl">Warteliste</h1>
          <div className="text-[10px] text-[#575148] mt-1">
            {rows.filter((r) => r.status === "wartend").length} wartend · {rows.filter((r) => r.status === "eingeladen").length} eingeladen
          </div>
        </div>
        <button onClick={load} disabled={busy} data-testid="waitlist-reload"
          className="inline-flex items-center gap-2 px-4 py-2 border border-[#D4AF37]/50 text-[#D4AF37] rounded text-[11px] uppercase tracking-widest hover:bg-[#D4AF37]/10">
          <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Neu laden
        </button>
      </div>

      {Object.keys(grouped).length === 0 && (
        <div className="card-lux p-10 text-center text-[#A8A398]">
          <Users size={28} className="mx-auto text-[#D4AF37]/60 mb-3" />
          Aktuell keine Wartelisten-Einträge
        </div>
      )}

      {Object.entries(grouped).map(([k, entries]) => (
        <div key={k} className="card-lux overflow-hidden" data-testid={`waitlist-group-${entries[0].showtime_id}`}>
          <div className="px-5 py-3 border-b border-[#1A1A1A] flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.32em] text-[#EEDC82]">{entries[0].movie_title}</div>
              <div className="text-xs text-[#A8A398] mt-0.5 font-mono">
                {entries[0].showtime_date} · {entries[0].showtime_start} · {entries[0].showtime_hall}
              </div>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-[#575148]">{entries.length} Eintrag/Einträge</div>
          </div>
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-[#0A0A0A] text-[10px] uppercase tracking-widest text-[#A8A398]">
              <tr><th className="text-left p-3">Gast</th><th className="text-left p-3">Kontakt</th><th className="text-left p-3">Sitze</th><th className="text-left p-3">Status</th><th className="text-left p-3">Erstellt</th><th className="text-right p-3">Aktion</th></tr>
            </thead>
            <tbody>
              {entries.map((r) => (
                <tr key={r.id} className="border-b border-[#141414] hover:bg-[#0C0C0C]/60" data-testid={`waitlist-row-${r.id}`}>
                  <td className="p-3">{r.guest_name}</td>
                  <td className="p-3 text-xs">
                    {r.guest_email && <div className="flex items-center gap-1 text-[#A8A398]"><Mail size={10} />{r.guest_email}</div>}
                    {r.guest_phone && <div className="flex items-center gap-1 text-[#A8A398] mt-0.5"><Phone size={10} />{r.guest_phone}</div>}
                  </td>
                  <td className="p-3 font-mono text-[#EEDC82]">{r.seat_count}</td>
                  <td className="p-3">
                    <span className={`text-[10px] uppercase tracking-widest border rounded-full px-2 py-0.5 ${r.status === "wartend" ? "border-[#EEDC82] text-[#EEDC82]" : "border-[#8FBC8F] text-[#8FBC8F]"}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="p-3 font-mono text-xs text-[#575148]">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    {r.status === "wartend" && r.guest_email && (
                      <button onClick={() => notify(r.id)} data-testid={`waitlist-notify-${r.id}`}
                        className="text-xs px-2 py-1 border border-[#8FBC8F]/50 text-[#8FBC8F] rounded hover:bg-[#8FBC8F]/10 inline-flex items-center gap-1 mr-2">
                        <Bell size={11} /> Benachrichtigen
                      </button>
                    )}
                    <button onClick={() => remove(r.id)}
                      className="text-xs px-2 py-1 border border-[#7A1921]/60 text-[#EE9CA1] rounded hover:bg-[#7A1921]/20 inline-flex items-center gap-1">
                      <Trash2 size={11} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      ))}

      <div className="text-[10px] text-[#575148] italic border-t border-[#1A1A1A] pt-3 flex items-center gap-2">
        <Ticket size={11} /> Auto-Benachrichtigung: Bei Storno einer Bestellung werden die passenden Wartelisten-Einträge automatisch per E-Mail eingeladen. Manuell mit „Benachrichtigen".
      </div>
    </div>
  );
}
